import React from 'react'
import { observer } from 'mobx-react-lite'

import { endOfMonth } from 'date-fns/endOfMonth'
import { isAfter } from 'date-fns/isAfter'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import { DateRange } from '@mui/x-date-pickers-pro'

import {
  ANALYTICS_EVENTS,
  Drawer,
  Loader,
  useAccountStore,
  useBudgetsStore,
  useCategoryStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { WidgetProps } from '../core'

import { AddBudgets } from './components/AddBudgets'
import { BubbleBudgets } from './components/BubbleBudgets'
import { BudgetDetailsDrawer } from './components/BudgetDetailsDrawer'
import { BudgetList } from './components/BudgetList'
import { Overview } from './components/Overview'

const BUDGET_LIST_WIDTH = 480

export const BubbleBudgetsWidget: React.FC<WidgetProps> = observer(
  ({ onBackClick, onMenuClick, sx }) => {
    const theme = useTheme()
    const { onEvent } = useEvent()
    const { visibleAccounts: accounts } = useAccountStore()
    const { budgets: copy, common: commonCopy } = useGlobalCopyStore()
    const { isInitialized, selectedAccounts, setDisplayedDate } = useGlobalUiStore()
    const {
      alert,
      dateRange,
      isDataLoaded,
      loadBudgetData,
      setDateRange,
      setAlert,
      setTheme,
      detailedBudgets: budgets,
    } = useBudgetsStore()
    const { setDateRange: setRecurringDateRange } = useRecurringTransactionsStore()
    const { loadDateRangeCategoryTotals, loadMonthlyCategoryTotals } = useCategoryStore()
    const { isSmallTablet, isMobile, isSmallMobile } = useScreenSize()
    const isColumnWidths = isSmallTablet || isMobile || isSmallMobile

    const [isAddOpen, setIsAddOpen] = React.useState(false)

    useWidgetLoadTimer({
      widgetName: 'BubbleBudgetsWidget',
      isLoaded: isDataLoaded,
    })

    React.useEffect(() => {
      setTheme(theme)
      if (!isDataLoaded) {
        loadBudgetData().finally(() => {
          onEvent(ANALYTICS_EVENTS.BUDGETS_VIEW)
        })
      }
    }, [])

    React.useEffect(() => {
      setDateRange({ start: startOfMonth(startOfToday()), end: endOfMonth(startOfToday()) })

      return () => {
        setDateRange({ start: startOfMonth(startOfToday()), end: endOfMonth(startOfToday()) })
      }
    }, [accounts])

    React.useEffect(() => {
      if (isInitialized) {
        setDisplayedDate(dateRange.start, dateRange.end)
        loadDateRangeCategoryTotals(selectedAccounts, dateRange.start, dateRange.end).finally()
        loadMonthlyCategoryTotals(selectedAccounts).finally()
        setRecurringDateRange(dateRange)
      }
    }, [isInitialized, selectedAccounts, dateRange])

    const onMonthChanged = (dateRange: DateRange<Date>) => {
      setDateRange({ start: dateRange[0] || new Date(), end: dateRange[1] || new Date() })
    }

    const handleAccountFilterClick = () => {
      onEvent(ANALYTICS_EVENTS.BUDGETS_CLICK_FILTER)
    }

    const handleAddClick = () => {
      setIsAddOpen(true)
      onEvent(ANALYTICS_EVENTS.BUDGETS_CLICK_ADD)
    }

    const handleBudgetAdded = () => {
      setIsAddOpen(false)
    }

    const handleBudgetsRecalculated = () => {
      setIsAddOpen(false)
    }

    const shouldDisableNext = isAfter(dateRange.end, startOfMonth(new Date()))

    if (!isInitialized || !isDataLoaded) return <Loader />

    return (
      <WidgetContainer
        actions={[
          {
            iconName: 'add',
            label: copy.add_title,
            onClick: handleAddClick,
          },
        ]}
        calendarActions={{
          onRangeChanged: onMonthChanged,
          shouldDisableNext,
          shouldDisablePicker: true,
        }}
        hasDivider={true}
        onAccountsFilterClick={handleAccountFilterClick}
        onBackClick={onBackClick}
        onMenuClick={onMenuClick}
        sx={sx}
        title={copy.budgets_title}
      >
        <Stack
          direction={!isColumnWidths ? 'row' : 'column'}
          height="100%"
          sx={{ justifyContent: 'center' }}
          width="100%"
        >
          <Stack height={!isColumnWidths ? 'calc(100dvh - 150px)' : 450}>
            <BubbleBudgets
              createBudgetOnClick={handleAddClick}
              height={!isColumnWidths ? 800 : 450}
              isDraggable={true}
              shouldShowZeroState={budgets.length === 0}
              unavailableWidth={!isColumnWidths ? BUDGET_LIST_WIDTH : 0}
            />
          </Stack>
          {budgets.length !== 0 && (
            <Stack
              gap={24}
              height="100%"
              sx={{ mr: 24, mt: 48 }}
              width={!isColumnWidths ? BUDGET_LIST_WIDTH : '100%'}
            >
              <Overview />
              <BudgetList />
            </Stack>
          )}
        </Stack>

        <BudgetDetailsDrawer />

        <Drawer
          ariaLabelClose={commonCopy.close_aria}
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          title={copy.add_new_title}
        >
          <AddBudgets
            onAddBudget={handleBudgetAdded}
            onRecalculateBudgets={handleBudgetsRecalculated}
          />
        </Drawer>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          autoHideDuration={3500}
          onClose={() => {
            setAlert(null)
          }}
          open={Boolean(alert)}
        >
          <Alert
            closeText=""
            onClose={() => {
              setAlert(null)
            }}
            severity={alert?.severity ?? 'success'}
            variant="filled"
          >
            {alert?.msg}
          </Alert>
        </Snackbar>
      </WidgetContainer>
    )
  },
)
